import React from 'react';
import styled, { css } from 'styled-components';
import { Trans, useTranslation } from 'next-i18next';
import Image from 'next/legacy/image';

import CustomButton from '@source/components/CustomButton/CustomButton';

import useGTMEvent from '@source/hooks/useGTMEvent';
import useWindowResize from '@source/hooks/useWindowResize';
import { INSPECTION_POINT } from '@source/pages/CarroCertified/constants';
import { IRegionProps } from '@source/interface';

const StyledWrapper = styled.section`
  margin-bottom: 35px;
  background-color: ${(props) => props.theme.color.background};

  .content-wrapper {
    padding: 12px 12px 39px 12px;

    @media screen and (min-width: 1280px) {
      padding: 51px 51px 0 51px;
    }

    .content-title {
      font-size: 24px;
      line-height: 32px;
      ${(props) => props.theme.typo.familyGoogle.semiBold}
      color: ${(props) => props.theme.color.primaryBase};
      margin-bottom: 12px;
    }

    .description {
      font-size: 14px;
      line-height: 22px;
      ${(props) => props.theme.typo.familyGoogle.medium}
      font-weight: 500;
      color: ${(props) => props.theme.color.onBackgroundHighEmphasis};
      margin-bottom: 30px;

      @media screen and (min-width: 576px) {
        margin-bottom: 40px;
      }
    }

    .link-btn {
      @media screen and (min-width: 375px) {
        margin: auto;
      }

      @media screen and (min-width: 576px) {
        margin: unset;
      }
    }
  }

  .inspection-images {
    padding-bottom: 32px;
  }
`;

const StyledButton = styled(CustomButton)`
  font-size: 14px;
  line-height: 120%;
  color: ${(props) => props.theme.color.primaryBase};
  text-transform: capitalize;
  color: ${(props) => props.theme.primaryColor};
  height: 42px;
  border-radius: 12px;

  &:hover {
    color: ${(props) => props.theme.color.primaryBase};
    border: 1px solid ${(props) => props.theme.color.primaryBase};
  }
`;

const StyledImageWrapper = styled.div<{ isBothEdge?: boolean; isMiddle?: boolean; width: number; height: number }>`
  display: flex;
  justify-content: flex-end;
  /* padding-left: 20px; */

  &:nth-child(even) {
    justify-content: flex-start;
    padding-left: 0;
    /* padding-right: 20px; */
  }

  &:last-child {
    margin-top: 50px;
  }

  ${(props) =>
    props.isBothEdge &&
    css`
      padding: 0 !important;
    `}

  ${(props) =>
    props.isMiddle &&
    css`
      justify-content: center !important;
    `}

    @media screen and (min-width: 576px) {
    &:last-child {
      margin-top: 81px;
    }
  }

  .image-wrapper-container {
    width: 100%;
    max-width: ${(props) => (props.width ? `${props.width}px` : '100%')};
  }

  .image-wrapper-item {
    position: relative;
    padding-bottom: ${(props) => (props.width && props.height ? `calc(${props.height / props.width} * 100%)` : '50%')};
  }
`;

type TGtmEventProps = {
  event: string;
  page_name: string;
};

type InspectionPointLink = {
  text: string;
  textMobile: string | '';
  url: string;
  gtmEvent: TGtmEventProps;
};

type InspectionPointImage = {
  key: string;
  width: number;
  height: number;
  url: string;
  alt: string;
  className?: string;
  isOnlyDesktop?: boolean;
  isBothEdge?: boolean;
  isOnlyMobile?: boolean;
  isMiddle?: boolean;
};

type TInspectionPointProps = {
  title: string;
  description: string;
  link: InspectionPointLink;
  images: InspectionPointImage[];
};

const CCPOPageInspection160Point = ({ region }: IRegionProps) => {
  const { pushGTMEvent } = useGTMEvent();
  const { t, i18n } = useTranslation('ccpo');
  const [windowWidth] = useWindowResize() as number[];
  const isMobile = windowWidth && windowWidth <= 576;

  const currentLang = region === 'sg' && i18n.language === 'ms-SG' ? 'ms' : i18n.language;

  const content = (INSPECTION_POINT(region, currentLang || 'en') as TInspectionPointProps) || {};
  const { title, description, link, images } = content;
  const inspectionLink = `/${region}/${currentLang}${link?.url}`;

  const desktopImages = images.filter((image) => !image?.isOnlyMobile);
  const mobileImages = images.filter((image) => !image?.isOnlyDesktop);

  const renderImage = ({ url, alt, width, height, isBothEdge, isMiddle, className, key }: InspectionPointImage) => (
    <StyledImageWrapper
      className={`image-wrapper ${className ? 'className' : ''}`}
      key={key}
      isBothEdge={isBothEdge}
      isMiddle={isMiddle}
      width={width}
      height={height}
      role="figure"
      aria-label={alt}
    >
      <div className="image-wrapper-container">
        <div className="image-wrapper-item">
          <Image alt={alt} src={url} layout="fill" aria-hidden="true" />
        </div>
      </div>
    </StyledImageWrapper>
  );

  const renderAllImages = (allImage: InspectionPointImage[]) =>
    allImage.map((image: InspectionPointImage) => renderImage(image));

  return (
    <StyledWrapper role="region" aria-label="160-Point Inspection Details">
      <article className="content-wrapper">
        <h2 className="content-title font-family-medium has-primary-color">{t(title)}</h2>
        <p className="description">
          <Trans
            ns="ccpo"
            defaults={t(description || '')}
            components={{ br: <br />, specificFont: <span style={{ fontFamily: 'Poppins' }} /> }}
          />
        </p>

        <StyledButton
          className="link-btn"
          variant="outline"
          onClick={() => {
            pushGTMEvent(link?.gtmEvent);
            window.open(inspectionLink);
          }}
          aria-label={t(link?.text || '')}
        >
          {isMobile &&
          ((region === 'id' && currentLang === 'id') || (['sg', 'my'].includes(region) && currentLang === 'ms')) ? (
            <Trans
              ns="ccpo"
              defaults={t(link.textMobile)}
              components={{ br: <br />, specificFont: <span style={{ fontFamily: 'Poppins' }} /> }}
            />
          ) : (
            t(link?.text || '')
          )}
        </StyledButton>
      </article>

      <figure className="inspection-images" role="group" aria-label="Inspection Images">
        {renderAllImages(isMobile ? mobileImages : desktopImages)}
      </figure>
    </StyledWrapper>
  );
};

export default CCPOPageInspection160Point;
